// Provider cards for the landing page: real, bookable Expy clinicians (US physical
// therapists + UK physiotherapists), same fields the find-a-physical-therapist carousel
// cards show — name, credentials, specialities, languages.
//
// Sources (all real, not stock):
//   - Richard Montmeny: the funnel's published provider (/public/providers).
//   - Fei / Emma / Adrian: the find-a-PT carousel cards (credentials + specialities
//     + languages as printed there); also active US booking agents.
//   - UK (Emmanuel / Andrew / Ryan / Leona): active agents on uk-book.expyhealth.com;
//     specialities summarised from their booking bios, all HCPC-registered to practise.
// Photos are self-hosted in public/providers so the page never hotlinks the booking hosts.
//
// Deliberately NON-interactive: no profile links. The page has ONE job, filling the funnel.
import type { CSSProperties, ReactNode } from 'react';
import { useIsNarrow } from '../lib/useIsNarrow';

type Provider = {
  name: string;
  specialties: string;
  languages: string;
  photo: string;
};

// No credentials/certs line: US "DPT/PT" and UK "HCPC-registered" read differently, so
// dropping them lets the US and UK clinicians blend on one page (John, 2026-09-27).
const PROVIDERS: Provider[] = [
  { name: 'Richard Montmeny', specialties: 'Orthopedic rehabilitation, Hand therapy, Work-related injury prevention', languages: 'English', photo: '/providers/richard.jpg' },
  { name: 'Fei Jiang', specialties: 'Orthopedics, Neurology, Sports Medicine', languages: 'English, Mandarin', photo: '/providers/fei.jpg' },
  { name: 'Emma Shapiro', specialties: 'Geriatrics, Neurology, Sports, Orthopedics', languages: 'English', photo: '/providers/emma.jpg' },
  { name: 'Adrian Miranda', specialties: 'Orthopedics, Sports Medicine', languages: 'English, Spanish', photo: '/providers/adrian.jpg' },
  { name: 'Emmanuel Udomhiaye', specialties: 'Musculoskeletal, Sports medicine, Injection therapy', languages: 'English', photo: '/providers/emmanuel.jpg' },
  { name: 'Andrew Henson', specialties: 'Musculoskeletal, Post-operative rehab, Sports', languages: 'English', photo: '/providers/andrew.jpg' },
  { name: 'Ryan Johnson', specialties: 'Musculoskeletal, Acupuncture, Spinal manipulation', languages: 'English', photo: '/providers/ryan.jpg' },
  { name: 'Leona Nwokeji', specialties: 'Musculoskeletal, Acupuncture, Orthopaedic medicine', languages: 'English', photo: '/providers/leona.jpg' },
];

const card: CSSProperties = { background: '#fff', borderRadius: 14, boxShadow: 'var(--card-shadow)' };

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--teal-deep)' }}>{label}</div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--slate-soft)', lineHeight: 1.4 }}>{value}</div>
    </div>
  );
}

export function ProviderStrip({ narrow }: { narrow?: boolean }) {
  const isNarrow = useIsNarrow();
  const phone = narrow ?? isNarrow;
  const avatar = 96;
  return (
    <section style={{ background: 'var(--surface)' }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: phone ? '48px 20px' : '72px 32px' }}>
        <H2 narrow={phone}>Licensed physical therapists behind Expy</H2>
        <p
          className="pretty"
          style={{ margin: '-16px auto 32px', maxWidth: 560, textAlign: 'center', fontSize: phone ? 15 : 16, lineHeight: 1.55, color: 'var(--slate-soft)' }}
        >
          Real clinicians build and guide your plan. When you want one, we match you with a licensed physical therapist in your area.
        </p>
        <ul
          style={{
            listStyle: 'none',
            margin: '0 auto',
            padding: 0,
            display: 'grid',
            gridTemplateColumns: phone ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 16,
            maxWidth: phone ? 400 : 1000,
          }}
        >
          {PROVIDERS.map((p) => (
            <li key={p.name} style={{ ...card, padding: 22, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div
                style={{
                  width: avatar,
                  height: avatar,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '3px solid var(--teal)',
                  background: 'var(--teal-tint)',
                  marginBottom: 12,
                }}
              >
                <img
                  src={p.photo}
                  alt={p.name}
                  loading="lazy"
                  width={avatar}
                  height={avatar}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--teal-deep)', lineHeight: 1.2 }}>{p.name}</div>
              <Field label="Specialities" value={p.specialties} />
              <Field label="Language(s)" value={p.languages} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function H2({ children, narrow }: { children: ReactNode; narrow: boolean }) {
  return (
    <h2 className="pretty" style={{ fontSize: narrow ? 26 : 32, fontWeight: 800, lineHeight: 1.2, margin: '0 0 28px', textAlign: 'center' }}>
      {children}
    </h2>
  );
}
